import { Component, inject, signal, effect, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '@/services/auth.service';
import { PermissoesService } from '@/services/permissoes.service';
import { DesbloqueioService } from '@/services/desbloqueio.service';

interface MenuItemWithPermission extends MenuItem {
    moduloNome?: string;
    items?: MenuItemWithPermission[];
}

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `<ul class="layout-menu">
        @for (item of filteredModel(); track $index) {
            @if (!item.separator) {
                <li app-menuitem [item]="item" [index]="$index" [root]="true"></li>
            } @else {
                <li class="menu-separator"></li>
            }
        }
    </ul> `
})
export class AppMenu implements OnInit, OnDestroy {
    private readonly authService = inject(AuthService);
    private readonly permissoesService = inject(PermissoesService);
    private readonly desbloqueioService = inject(DesbloqueioService);

    private readonly desbloqueiosPendentes = signal<number>(0);
    private refreshSubscription?: Subscription;

    private readonly allMenuItems: MenuItemWithPermission[] = [
        {
            label: 'Administrativo',
            items: [
                {
                    label: 'Usuário',
                    icon: 'pi pi-fw pi-users',
                    routerLink: ['/pages/usuario'],
                    moduloNome: 'Usuários'
                },
                {
                    label: 'Módulos',
                    icon: 'pi pi-fw pi-box',
                    routerLink: ['/pages/modulo'],
                    moduloNome: 'Módulos'
                },
                {
                    label: 'Distrito',
                    icon: 'pi pi-fw pi-globe',
                    routerLink: ['/pages/distrito'],
                    moduloNome: 'Distritos'
                },
                {
                    label: 'Departamento',
                    icon: 'pi pi-fw pi-building',
                    routerLink: ['/pages/departamento'],
                    moduloNome: 'Departamentos'
                },
                {
                    label: 'Região',
                    icon: 'pi pi-fw pi-map',
                    routerLink: ['/pages/regiao'],
                    moduloNome: 'Regiões'
                },
                {
                    label: 'Eventos',
                    icon: 'pi pi-fw pi-calendar',
                    routerLink: ['/pages/evento'],
                    moduloNome: 'Eventos'
                },
                {
                    label: 'Templates de Etiqueta',
                    icon: 'pi pi-fw pi-tags',
                    routerLink: ['/pages/template-etiqueta'],
                    moduloNome: 'Eventos'
                },
                {
                    label: 'Impressoras',
                    icon: 'pi pi-fw pi-print',
                    routerLink: ['/pages/impressora'],
                    moduloNome: 'Eventos'
                }
            ]
        },
        {
            label: 'Eventos',
            items: [
                {
                    label: 'Participantes',
                    icon: 'pi pi-fw pi-id-card',
                    routerLink: ['/pages/participantes-evento'],
                    moduloNome: 'EventosParticipantes'
                }
            ]
        },
        {
            label: 'Motoclube',
            items: [
                {
                    label: 'Registro de Regiões',
                    icon: 'pi pi-fw pi-id-card',
                    routerLink: ['/pages/registro-regiao'],
                    moduloNome: 'RegistroRegioes'
                },
                {
                    label: 'Tesoureiro',
                    icon: 'pi pi-fw pi-sitemap',
                    routerLink: ['/pages/motocoordenadoria'],
                    moduloNome: 'MotoClubeTesouraria'
                },
                {
                    label: 'Registros',
                    icon: 'pi pi-fw pi-file-edit',
                    routerLink: ['/pages/motoregistro'],
                    moduloNome: 'MotoClubeRegistros'
                },
                {
                    label: 'Acompanhamento',
                    icon: 'pi pi-fw pi-chart-bar',
                    routerLink: ['/pages/motoregistro-acompanhamento'],
                    moduloNome: 'MotoClubeAdministrador'
                },
                {
                    label: 'Termo de Quitação',
                    icon: 'pi pi-fw pi-file-check',
                    routerLink: ['/pages/motoregistro-termo-quitacao'],
                    moduloNome: 'MotoClubeAdministrador'
                },
                {
                    label: 'Desbloqueios',
                    icon: 'pi pi-fw pi-unlock',
                    routerLink: ['/pages/desbloqueio'],
                    moduloNome: 'MotoClubeDesbloqueio'
                }
            ]
        }
    ];

    protected readonly filteredModel = signal<MenuItem[]>([]);

    constructor() {
        effect(() => {
            const currentUser = this.authService.currentUser();
            const pendentes = this.desbloqueiosPendentes();

            if (currentUser) {
                this.filteredModel.set(this.filterMenuItems(this.allMenuItems, currentUser, pendentes));
            } else {
                this.filteredModel.set([]);
            }
        });
    }

    private changeSubscription?: Subscription;

    ngOnInit(): void {
        this.loadDesbloqueiosPendentes();

        // Escutar mudanças nos desbloqueios (quando aprovar/reprovar/criar/cancelar)
        this.changeSubscription = this.desbloqueioService.onDesbloqueioChange$.subscribe(() => {
            this.loadDesbloqueiosPendentes();
        });

        // Atualizar a cada 60 segundos como fallback
        this.refreshSubscription = interval(60000)
            .pipe(switchMap(() => this.desbloqueioService.listarPendentes('MOTOCLUBE')))
            .subscribe({
                next: (pendentes) => {
                    this.desbloqueiosPendentes.set(pendentes.length);
                },
                error: () => {
                    // Silenciar erros no polling
                }
            });
    }

    ngOnDestroy(): void {
        this.refreshSubscription?.unsubscribe();
        this.changeSubscription?.unsubscribe();
    }

    private loadDesbloqueiosPendentes(): void {
        this.desbloqueioService.listarPendentes('MOTOCLUBE').subscribe({
            next: (pendentes) => {
                this.desbloqueiosPendentes.set(pendentes.length);
            },
            error: () => {
                this.desbloqueiosPendentes.set(0);
            }
        });
    }

    private filterMenuItems(items: MenuItemWithPermission[], currentUser: any, pendentes: number): MenuItem[] {
        return items
            .map((item) => {
                if (item.items && item.items.length > 0) {
                    const filteredSubItems = item.items
                        .filter((subItem) => {
                            if (!subItem.moduloNome) {
                                return true;
                            }

                            return this.permissoesService.hasPermission(currentUser, subItem.moduloNome);
                        })
                        .map((subItem) => {
                            // Adicionar badge ao item de desbloqueio
                            if (subItem.moduloNome === 'MotoClubeDesbloqueio' && pendentes > 0) {
                                return {
                                    ...subItem,
                                    badge: pendentes.toString(),
                                    badgeStyleClass: 'menu-badge menu-badge-danger'
                                };
                            }

                            return subItem;
                        });

                    if (filteredSubItems.length > 0) {
                        return {
                            ...item,
                            items: filteredSubItems
                        };
                    }

                    return null;
                }

                if (item.moduloNome) {
                    return this.permissoesService.hasPermission(currentUser, item.moduloNome) ? item : null;
                }

                return item;
            })
            .filter((item) => item !== null) as MenuItem[];
    }
}
